import { getSelectedCurrency } from '@/src/redux/slices/currencySlice'
import {
    setOnwardCheckedBaggageIncludedRecommendedFilter,
    setOnwardHideBudgetAirlinesRecommendedFilter,
    setReturnCheckedBaggageIncludedRecommendedFilter,
    setReturnHideBudgetAirlinesRecommendedFilter
} from '@/src/redux/slices/srpStatesSlice'
import { useAppSelector } from '@/src/redux/store'
import type { AIRecommendationSuggestionsResponse } from '@/src/types/aiRecommendation'
import { getConversionRateDetails, getCurrencyByLocale } from '@/src/utils/currency/formatCurrency'
import { isEmpty } from '@/src/utils/generalUtils'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useThemedToast } from '../../ui/useThemedToast'
import { useClearFilters } from '../clearFilters'
import { useGetSearchParamsObj } from '../searchParams'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

const hasNumber = (value: unknown): value is number => typeof value === 'number' && !isNaN(value)
const normalizeCode = (value: string) => value?.trim().toUpperCase()

export function useApplySuggestedFilters () {
    const dispatch = useDispatch()
    const updateCache = useUpdateSearchResultCache()
    const { filterData, selectedJourneyLeg } = useSearchResults()
    const { isFilterApplied, handleClearAllFilters } = useClearFilters()
    const toCurrency = useAppSelector(getSelectedCurrency)
    const { currency: fromCurrency } = getCurrencyByLocale()
    const { urlQueryObj } = useGetSearchParamsObj()
    const searchFrom = urlQueryObj?.from?.toUpperCase()
    const searchTo = urlQueryObj?.to?.toUpperCase()
    const { showInfo } = useThemedToast()
    const tCommon = useTranslations('common')

    const applySuggestedFilters = useCallback(
        (payload: AIRecommendationSuggestionsResponse | null) => {
            if (isFilterApplied) {
                handleClearAllFilters()
            }
            const filtersToApply = payload?.suggestedFilters ?? null
            if (!filtersToApply || !filterData) return

            const leg = selectedJourneyLeg as 0 | 1
            const unavailableByType: Record<string, string[]> = {}
            const collectUnavailable = (type: string, values: string[]) => {
                if (!values.length) return
                unavailableByType[type] = [...(unavailableByType[type] || []), ...values]
            }

            updateCache(currentData => {
                if (!currentData?.filterData) return currentData as NonNullable<typeof currentData>

                const data = currentData
                data.smartRecommendationData[leg].suggestedFilters = filtersToApply
                data.smartRecommendationData[leg].weights = payload?.weights ?? null
                data.smartRecommendationData[leg].preferences = payload?.preferences ?? null
                data.smartRecommendationData[leg].priority = payload?.priority ?? null
                data.smartRecommendationData[leg].sortPreference = payload?.sortPreference ?? null
                //filter data apply
                if (filtersToApply.nonStop === true) {
                    if (data.filterData.NewStops?.[leg]) {
                        data.filterData.NewStops[leg].selectedValues = [0]
                    }
                } else if (filtersToApply.stops?.length) {
                    if (data.filterData.NewStops?.[leg]) {
                        data.filterData.NewStops[leg].selectedValues = [...filtersToApply.stops]
                    }
                }

                if (filtersToApply.departureTimeSlots?.length) {
                    if (data.filterData.departureTimeFrom?.[leg]) {
                        data.filterData.departureTimeFrom[leg].selectedValues = [...filtersToApply.departureTimeSlots]
                    }
                }

                if (filtersToApply.arrivalTimeSlots?.length) {
                    if (data.filterData.arrivalTimeFrom?.[leg]) {
                        data.filterData.arrivalTimeFrom[leg].selectedValues = [...filtersToApply.arrivalTimeSlots]
                    }
                }

                const airlineOptions = data.filterData.airlineFilter?.[leg]?.options?.map(o => o.value) ?? []
                if (filtersToApply.airlineCodes?.length && airlineOptions.length) {
                    const validAirlineCodes = new Set(airlineOptions.map(code => normalizeCode(code)))
                    const requestedAirlineCodes = filtersToApply.airlineCodes.map(code => normalizeCode(code))
                    const valid = requestedAirlineCodes.filter(code => validAirlineCodes.has(code))
                    const invalid = requestedAirlineCodes.filter(code => !validAirlineCodes.has(code))
                    collectUnavailable('airline', invalid)
                    if (valid.length) {
                        if (data.filterData.airlineFilter?.[leg]) {
                            data.filterData.airlineFilter[leg].selectedValues = Array.from(new Set(valid))
                        }
                    }
                }

                if (
                    filtersToApply.priceRange &&
                    (filtersToApply.priceRange.min != null || filtersToApply.priceRange.max != null)
                ) {
                    const target = data.filterData.journeyPrice?.[leg]
                    if (target?.current) {
                        const { currencyList = [] } = currentData?.jsons || {}
                        const conversionRateDetails = isEmpty(currencyList)
                            ? { rate: 1, buffer: 0 }
                            : getConversionRateDetails(currencyList, fromCurrency, toCurrency)
                        const { rate = 1, buffer = 0 } = conversionRateDetails || {}
                        const conversionFactor = rate + (rate * buffer) / 100

                        const suggestedMin = filtersToApply.priceRange.min != null
                            ? Math.floor(Number(filtersToApply.priceRange.min) / conversionFactor)
                            : target.min
                        const suggestedMax = filtersToApply.priceRange.max != null
                            ? Math.ceil(Number(filtersToApply.priceRange.max) / conversionFactor)
                            : target.max

                        const min = Math.max(suggestedMin, target.min)
                        const max = Math.min(suggestedMax, target.max)
                        target.current = { min, max }
                    }
                }

                if (
                    filtersToApply.tripDurationRange &&
                    (filtersToApply.tripDurationRange.min != null || filtersToApply.tripDurationRange.max != null)
                ) {
                    const target = data.filterData.tripDuration?.[leg]
                    if (target?.current) {
                        const rawMin = Number(filtersToApply.tripDurationRange.min)
                        const rawMax = Number(filtersToApply.tripDurationRange.max)
                        const min = hasNumber(rawMin) ? Math.max(rawMin, target.min) : target.min
                        const max = hasNumber(rawMax) ? Math.min(rawMax, target.max) : target.max
                        target.current = { min, max: Math.max(min, max) }
                    }
                }

                if (
                    filtersToApply.layoverDurationRange &&
                    (filtersToApply.layoverDurationRange.min != null || filtersToApply.layoverDurationRange.max != null)
                ) {
                    const target = data.filterData.layoverDuration?.[leg]
                    if (target?.current) {
                        const rawMin = Number(filtersToApply.layoverDurationRange.min)
                        const rawMax = Number(filtersToApply.layoverDurationRange.max)
                        const min = hasNumber(rawMin) ? Math.max(rawMin, target.min) : target.min
                        const max = hasNumber(rawMax) ? Math.min(rawMax, target.max) : target.max
                        target.current = { min, max: Math.max(min, max) }
                    }
                }

                if (filtersToApply.layoverAirportCodes?.length && data.filterData.layover_Airport?.[leg]) {
                    const layoverTarget = data.filterData.layover_Airport[leg]
                    const validLayoverCodes = new Set((layoverTarget.values || []).map(code => normalizeCode(code)))
                    const selectedLayoverCodes = filtersToApply.layoverAirportCodes
                        .map(code => normalizeCode(code))
                        .filter(code => validLayoverCodes.has(code))
                    const invalidLayoverCodes = filtersToApply.layoverAirportCodes
                        .map(code => normalizeCode(code))
                        .filter(code => !validLayoverCodes.has(code))
                    collectUnavailable('layover airport', invalidLayoverCodes)
                    layoverTarget.selectedOptions = Array.from(new Set(selectedLayoverCodes))
                }

                // Conflict safeguard: direct flights cannot have layover-specific constraints.
                if (filtersToApply.nonStop === true) {
                    if (data.filterData.layover_Airport?.[leg]) {
                        data.filterData.layover_Airport[leg].selectedOptions = []
                    }
                    if (data.filterData.layoverDuration?.[leg]) {
                        data.filterData.layoverDuration[leg].current = {
                            min: data.filterData.layoverDuration[leg].min,
                            max: data.filterData.layoverDuration[leg].max
                        }
                    }
                }

                const nbGroups = data.filterData.nbAirportFilter?.[leg] || []
                const included = filtersToApply.includedAirportCodes
                if (included && nbGroups.length) {
                    const { departureAirportCodes = [], arrivalAirportCodes = [] } = included
                    const departureCity = leg === 0 ? searchFrom : searchTo
                    const arrivalCity = leg === 0 ? searchTo : searchFrom

                    nbGroups.forEach(group => {
                        const code = group.airportCode?.toUpperCase()
                        const validCodes = new Set<string>([
                            normalizeCode(group.airportCode),
                            ...(group.options || []).map(option => normalizeCode(option.value))
                        ])
                        if (code === departureCity) {
                            const requested = departureAirportCodes.map(value => normalizeCode(value))
                            const valid = requested.filter(value => validCodes.has(value))
                            const invalid = requested.filter(value => !validCodes.has(value))
                            collectUnavailable('nearby departure airport', invalid)
                            group.selectedValues = Array.from(new Set(valid))
                        } else if (code === arrivalCity) {
                            const requested = arrivalAirportCodes.map(value => normalizeCode(value))
                            const valid = requested.filter(value => validCodes.has(value))
                            const invalid = requested.filter(value => !validCodes.has(value))
                            collectUnavailable('nearby arrival airport', invalid)
                            group.selectedValues = Array.from(new Set(valid))
                        }
                    })
                }

                return data as NonNullable<typeof currentData>
            })



            if (filtersToApply.checkedBaggageIncluded === true) {
                leg === 0
                    ? dispatch(setOnwardCheckedBaggageIncludedRecommendedFilter(true))
                    : dispatch(setReturnCheckedBaggageIncludedRecommendedFilter(true))
            }
            if (filtersToApply.hideBudgetAirlines === true) {
                leg === 0
                    ? dispatch(setOnwardHideBudgetAirlinesRecommendedFilter(true))
                    : dispatch(setReturnHideBudgetAirlinesRecommendedFilter(true))
            }

            const unavailableEntries = Object.entries(unavailableByType)
                .map(([type, values]) => {
                    const uniqueValues = Array.from(new Set(values))
                    return uniqueValues.length ? `${type}: ${uniqueValues.join(', ')}` : ''
                })
                .filter(Boolean)

            if (unavailableEntries.length) {
                showInfo(
                    tCommon('aiUnavailableFiltersToast', { details: unavailableEntries.join(' | ') }),
                    {
                        duration: 6000,
                        position: 'top-center',

                    }
                )
            }
        },
        [filterData, selectedJourneyLeg, updateCache, dispatch, isFilterApplied, handleClearAllFilters, fromCurrency, toCurrency, searchFrom, searchTo, showInfo, tCommon]
    )

    return { applySuggestedFilters, }
}
